'use client';

/**
 * ToolbarFilterChip Component
 *
 * Compact chip-style filter select for DataToolbar.
 * Shows label when no value, shows selected value when active.
 * Opens menu on click for selection.
 */

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useCallback, useId, useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import type { ToolbarDensity, ToolbarFilterChipProps, ToolbarFilterOption } from '../../types/dataToolbar';

// ============================================================================
// Icons
// ============================================================================

const ChevronDownIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
  </svg>
);

const CheckIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

// ============================================================================
// Styled Components
// ============================================================================

interface StyledChipButtonProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $density: ToolbarDensity;
  $hasValue: boolean;
  $isOpen: boolean;
}

const StyledChipButton = styled('button', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledChipButtonProps>(({ theme, $isDark, $neonAccent, $density, $hasValue, $isOpen }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const glass = glassEffects[$isDark ? 'dark' : 'light'];
  const height = $density === 'compact' ? 28 : 32;
  const activeColor = neonStyles?.base ?? theme.palette.primary.main;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height,
    padding: '0 12px',
    border: 'none',
    borderRadius: 16,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.75rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    transition: transitionPresets.allFast,
    outline: 'none',
    backdropFilter: 'blur(12px) saturate(180%)',
    WebkitBackdropFilter: 'blur(12px) saturate(180%)',

    // Default state — glass background
    backgroundColor: $hasValue ? alpha(activeColor, $isDark ? 0.15 : 0.1) : glass.background,
    color: $hasValue ? activeColor : $isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',

    // Border — thin glass border with inset specular highlight
    boxShadow: $hasValue
      ? `inset 0 0 0 0.5px ${alpha(activeColor, 0.35)}, inset 0 0.5px 0 ${alpha('rgb(255,255,255)', $isDark ? 0.1 : 0.4)}`
      : `inset 0 0 0 0.5px ${glass.border}, inset 0 0.5px 0 ${alpha('rgb(255,255,255)', $isDark ? 0.1 : 0.4)}`,

    // Hover — glass hover background
    '&:hover': {
      backgroundColor: $hasValue ? alpha(activeColor, $isDark ? 0.2 : 0.15) : glass.backgroundHover,
      boxShadow: $hasValue
        ? `inset 0 0 0 0.5px ${alpha(activeColor, 0.5)}, inset 0 0.5px 0 ${alpha('rgb(255,255,255)', $isDark ? 0.12 : 0.5)}`
        : `inset 0 0 0 0.5px ${glass.borderHover}, inset 0 0.5px 0 ${alpha('rgb(255,255,255)', $isDark ? 0.12 : 0.5)}`,
    },

    // Focus
    '&:focus-visible': {
      boxShadow: `inset 0 0 0 1px ${activeColor}, 0 0 0 2px ${alpha(activeColor, 0.25)}`,
    },

    // Open state — glass with accent tint
    ...($isOpen && {
      backgroundColor: alpha(activeColor, $isDark ? 0.15 : 0.1),
      boxShadow: `inset 0 0 0 0.5px ${alpha(activeColor, 0.5)}, 0 0 12px ${alpha(activeColor, 0.12)}`,
    }),

    // Disabled
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },

    // Chevron rotation
    '& .chevron': {
      transition: 'transform 0.2s ease',
      transform: $isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      opacity: 0.6,
    },
  };
});

interface StyledMenuProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledMenu = styled(Menu, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledMenuProps>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const glass = glassEffects[$isDark ? 'dark' : 'light'];

  return {
    '& .MuiPaper-root': {
      marginTop: 4,
      minWidth: 140,
      maxWidth: 240,
      borderRadius: 12,
      backgroundColor: glass.background,
      backdropFilter: glass.backdropBlur,
      WebkitBackdropFilter: glass.backdropBlur,
      border: `0.5px solid ${glass.border}`,
      boxShadow: glass.shadow,

      '& .MuiList-root': {
        padding: 4,
      },

      '& .MuiMenuItem-root': {
        borderRadius: 6,
        margin: '2px 0',
        padding: '6px 12px',
        fontSize: '0.8125rem',
        minHeight: 32,
        transition: transitionPresets.allFast,

        '&:hover': {
          backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        },

        '&.Mui-selected': {
          backgroundColor: neonStyles
            ? alpha(neonStyles.base, $isDark ? 0.15 : 0.1)
            : alpha(theme.palette.primary.main, $isDark ? 0.15 : 0.1),
          color: neonStyles?.base ?? theme.palette.primary.main,

          '&:hover': {
            backgroundColor: neonStyles
              ? alpha(neonStyles.base, $isDark ? 0.2 : 0.15)
              : alpha(theme.palette.primary.main, $isDark ? 0.2 : 0.15),
          },
        },
      },
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const ToolbarFilterChip: React.FC<ToolbarFilterChipProps> = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  neonAccent,
  density = 'comfortable',
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const menuId = useId();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      handleClose();
    },
    [onChange, handleClose],
  );

  // Find selected option label
  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption && value ? selectedOption.label : label;
  const hasValue = Boolean(value && selectedOption);

  return (
    <>
      <StyledChipButton
        $isDark={isDark}
        $neonAccent={neonAccent}
        $density={density}
        $hasValue={hasValue}
        $isOpen={isOpen}
        onClick={handleClick}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
      >
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 1,
            maxWidth: 100,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayLabel}
        </Typography>
        <Box className="chevron" sx={{ display: 'flex', alignItems: 'center', ml: -0.25 }}>
          <ChevronDownIcon size={density === 'compact' ? 14 : 16} />
        </Box>
      </StyledChipButton>

      <StyledMenu
        id={menuId}
        $isDark={isDark}
        $neonAccent={neonAccent}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
          },
        }}
      >
        {options.map((option: ToolbarFilterOption) => (
          <MenuItem key={option.value} selected={option.value === value} onClick={() => handleSelect(option.value)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              {option.icon && <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.7 }}>{option.icon}</Box>}
              <Typography
                component="span"
                sx={{
                  flex: 1,
                  fontSize: 'inherit',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {option.label}
              </Typography>
              {option.value === value && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, opacity: 0.8 }}>
                  <CheckIcon size={14} />
                </Box>
              )}
            </Box>
          </MenuItem>
        ))}
      </StyledMenu>
    </>
  );
};

ToolbarFilterChip.displayName = 'ToolbarFilterChip';

export default ToolbarFilterChip;
